import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import API_CONSTRUCTOR from "../../models/api-model";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Loader2, Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { encodeDealFinderCalcQuery } from "../../utils/dealFinderCalcQuery";
import { AutoCompleteAddress } from "../../components/property-search/auto-complete-address";

function parseCompPropertyIds(row) {
  const raw = row.comp_property_ids;
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p.filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Good/bad tables: highest flip gross profit first; missing profit sorts last. */
function sortRowsByGrossProfitDesc(rows) {
  const gp = (r) => {
    const v = Number((r.metrics_json || {}).grossProfit);
    return Number.isFinite(v) ? v : null;
  };
  return [...rows].sort((a, b) => {
    const ga = gp(a);
    const gb = gp(b);
    if (ga == null && gb == null) return 0;
    if (ga == null) return 1;
    if (gb == null) return -1;
    return gb - ga;
  });
}

const api = API_CONSTRUCTOR();

const TIER_OPTIONS = [
  { value: "1", label: "Tier 1 — Ultra high cost ($700–$1,400+ / sqft, default $1,050)" },
  { value: "2", label: "Tier 2 — High cost ($500–$900 / sqft, default $700)" },
  { value: "3", label: "Tier 3 — Medium ($350–$650 / sqft, default $500)" },
  { value: "4", label: "Tier 4 — Lower ($250–$500 / sqft, default $375)" },
];

function formatMoney(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatMoneyDetailed(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function BreakdownRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-border/60">
      <span className="text-muted-foreground pr-2">{label}</span>
      <span className="font-medium tabular-nums text-right shrink-0">{value}</span>
    </div>
  );
}

export default function AdminDealFinder() {
  const [mode, setMode] = useState("anchor");
  const [anchorPropertyId, setAnchorPropertyId] = useState("");
  const [anchorPropertyLabel, setAnchorPropertyLabel] = useState("");
  const [geoSearchTarget, setGeoSearchTarget] = useState(null);
  const [geoRegionLabel, setGeoRegionLabel] = useState("");
  const [addressPickerKey, setAddressPickerKey] = useState(0);
  const [zip, setZip] = useState("");
  const [county, setCounty] = useState("");
  const [state, setState] = useState("");
  const [constructionTier, setConstructionTier] = useState("4");
  const [costOverride, setCostOverride] = useState("");
  const [radiusMiles, setRadiusMiles] = useState("0.75");
  const [marketFilter, setMarketFilter] = useState("all");
  const [maxCandidates, setMaxCandidates] = useState("100");
  const [submitting, setSubmitting] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [resultsGood, setResultsGood] = useState([]);
  const [resultsBad, setResultsBad] = useState([]);
  const [resultsSkipped, setResultsSkipped] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const pollRef = useRef(null);
  const [flipModal, setFlipModal] = useState(null);

  const openFlipBreakdownModal = useCallback((m) => {
    if (!m?.flipBreakdown || typeof m.flipBreakdown !== "object") {
      toast.message(
        "Flip breakdown is only available for scans run after the latest update. Re-run the job to see line items."
      );
      return;
    }
    setFlipModal({
      address: m.address,
      propertyId: m.property_id,
      breakdown: m.flipBreakdown,
      engineGrossProfitLegacy: m.engineGrossProfitLegacy,
    });
  }, []);

  const loadRecentJobs = useCallback(async () => {
    try {
      const res = await api.get("/admin/deal-finder/jobs?limit=15");
      if (res.success) setRecentJobs(res.data || []);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadRecentJobs();
  }, [loadRecentJobs]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const fetchResults = async (jobId) => {
    const [goodRes, badRes, skipRes] = await Promise.all([
      api.get(`/admin/deal-finder/jobs/${jobId}/results?verdict=good`),
      api.get(`/admin/deal-finder/jobs/${jobId}/results?verdict=bad`),
      api.get(`/admin/deal-finder/jobs/${jobId}/results?verdict=skipped`),
    ]);
    if (goodRes.success) setResultsGood(goodRes.data || []);
    if (badRes.success) setResultsBad(badRes.data || []);
    if (skipRes.success) setResultsSkipped(skipRes.data || []);
  };

  const pollJob = useCallback(
    async (jobId) => {
      try {
        const res = await api.get(`/admin/deal-finder/jobs/${jobId}`);
        if (!res.success) return;
        setJobStatus(res.data);
        if (res.data.status === "completed" || res.data.status === "failed") {
          stopPolling();
          setSubmitting(false);
          await fetchResults(jobId);
          loadRecentJobs();
          if (res.data.status === "failed") {
            toast.error(res.data.error || "Deal finder job failed");
          } else {
            toast.success("Deal finder finished");
          }
        }
      } catch (e) {
        stopPolling();
        setSubmitting(false);
        toast.error(e.response?.data?.error || "Failed to load job status");
      }
    },
    [loadRecentJobs]
  );

  useEffect(() => {
    return () => stopPolling();
  }, []);

  const startPolling = (jobId) => {
    stopPolling();
    pollRef.current = setInterval(() => pollJob(jobId), 2500);
    pollJob(jobId);
  };

  const handleModeChange = (value) => {
    setMode(value);
    setAddressPickerKey((k) => k + 1);
    if (value === "anchor") {
      setGeoSearchTarget(null);
      setGeoRegionLabel("");
      setZip("");
      setCounty("");
      setState("");
    } else {
      setAnchorPropertyId("");
      setAnchorPropertyLabel("");
    }
  };

  const clearGeoAutocompleteSelection = () => {
    setGeoSearchTarget(null);
    setGeoRegionLabel("");
  };

  const handleAnchorAddressSelect = (option) => {
    const pid = option.propertyId;
    if (!pid) {
      toast.error('Pick a property (house icon), not a region. Switch to "Geography only" to scan by ZIP or area.');
      return;
    }
    setAnchorPropertyId(String(pid).trim());
    setAnchorPropertyLabel(String(option.label || pid));
  };

  const handleGeoAddressSelect = (option) => {
    if (option.propertyId) {
      toast.error('Pick a region (map pin), not a single property. Use "Anchor property" to run from one home.');
      return;
    }
    setGeoSearchTarget({ ...option });
    setGeoRegionLabel(option.label || "");
    setZip("");
    setCounty("");
    setState("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const anchorIdStr = String(anchorPropertyId ?? "").trim();
    if (mode === "anchor") {
      if (!anchorIdStr) {
        toast.error("Search and select a property to set the anchor.");
        return;
      }
    }
    if (mode === "geo") {
      const hasZip = zip.trim().length > 0;
      const hasCounty = county.trim() && state.trim();
      const hasRegionTarget =
        geoSearchTarget && typeof geoSearchTarget === "object" && Object.keys(geoSearchTarget).length > 0;
      if (!hasZip && !hasCounty && !hasRegionTarget) {
        toast.error("Search and select a region, or enter a ZIP, or county and state.");
        return;
      }
    }
    setSubmitting(true);
    setResultsGood([]);
    setResultsBad([]);
    setResultsSkipped([]);
    setJobStatus(null);
    try {
      const hasGeoTarget =
        mode === "geo" &&
        geoSearchTarget &&
        typeof geoSearchTarget === "object" &&
        Object.keys(geoSearchTarget).length > 0;
      const body = {
        mode,
        anchorPropertyId: mode === "anchor" ? anchorIdStr : undefined,
        zip: mode === "geo" && zip.trim() ? zip.trim() : undefined,
        county: mode === "geo" && county.trim() ? county.trim() : undefined,
        state: mode === "geo" && state.trim() ? state.trim() : undefined,
        geoSearchTarget: mode === "geo" && hasGeoTarget ? geoSearchTarget : undefined,
        constructionTier: parseInt(constructionTier, 10),
        costPerSqftOverride: costOverride.trim() ? parseFloat(costOverride) : undefined,
        radiusMiles: parseFloat(radiusMiles) || 0.75,
        marketFilter,
        maxCandidatesEvaluate: parseInt(maxCandidates, 10) || 100,
        rentalPeriodMonths: 18,
        investorReturnRate: 15,
        upfrontPaymentPct: 30,
        investorDownPaymentPct: 30,
        paymentParts: 4,
      };
      const res = await api.post("/admin/deal-finder/jobs", body);
      if (!res.success) throw new Error(res.error || "Failed to start job");
      setActiveJobId(res.data.id);
      startPolling(res.data.id);
      toast.message("Scan started — results will appear when complete.");
    } catch (err) {
      setSubmitting(false);
      toast.error(err.response?.data?.error || err.message || "Request failed");
    }
  };

  const loadJobFromHistory = async (jobId) => {
    setActiveJobId(jobId);
    setSubmitting(false);
    stopPolling();
    try {
      const res = await api.get(`/admin/deal-finder/jobs/${jobId}`);
      if (res.success) {
        setJobStatus(res.data);
        await fetchResults(jobId);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to load job");
    }
  };

  const summary = jobStatus?.summary_json;
  const params = jobStatus?.params_json;

  const renderResultsTable = (rows, verdictLabel) => {
    const jobParams = params || {};
    if (!rows.length) {
      return (
        <p className="text-sm text-muted-foreground py-6 text-center">
          No {verdictLabel} rows for this run.
        </p>
      );
    }
    const displayRows =
      verdictLabel === "good" || verdictLabel === "bad"
        ? sortRowsByGrossProfitDesc(rows)
        : rows;
    return (
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Property</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Sqft</TableHead>
              <TableHead>$/sqft (implied)</TableHead>
              <TableHead>Comp $/sqft</TableHead>
              <TableHead>Comp IDs</TableHead>
              <TableHead>Est. sale</TableHead>
              <TableHead className="whitespace-nowrap">Flipping cost</TableHead>
              <TableHead>Gross profit</TableHead>
              <TableHead>Rent</TableHead>
              <TableHead className="w-[52px]" title="Open property (deal calc preloaded when available)">
                Open
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayRows.map((r) => {
              const m = r.metrics_json || {};
              const compIds = parseCompPropertyIds(r);
              const metricsForEncode = {
                ...m,
                investorReturnRate: m.investorReturnRate ?? jobParams.investorReturnRate,
                ownerReturnRate: m.ownerReturnRate ?? jobParams.ownerReturnRate,
                rentalPeriodMonths: m.rentalPeriodMonths ?? jobParams.rentalPeriodMonths,
                upfrontPaymentPct: m.upfrontPaymentPct ?? jobParams.upfrontPaymentPct,
                paymentParts: m.paymentParts ?? jobParams.paymentParts,
              };
              const calcToken = encodeDealFinderCalcQuery(metricsForEncode);
              const propertyHref =
                calcToken && r.property_id
                  ? `/property/${encodeURIComponent(r.property_id)}?dealCalc=${encodeURIComponent(calcToken)}`
                  : `/property/${encodeURIComponent(r.property_id || "")}`;
              const handleRowOpen = (event) => {
                if (!r.property_id) return;
                const interactiveEl = event.target.closest("a, button, input, select, textarea");
                if (interactiveEl) return;
                window.open(propertyHref, "_blank", "noopener,noreferrer");
              };

              return (
                <TableRow
                  key={r.id}
                  onClick={handleRowOpen}
                  className={r.property_id ? "cursor-pointer" : undefined}
                >
                  <TableCell className="max-w-[200px]">
                    <div className="text-xs font-medium truncate" title={m.address}>
                      {m.address || r.property_id}
                    </div>
                    {r.reason_codes?.length > 0 && (
                      <div className="text-[10px] text-muted-foreground mt-1">
                        {r.reason_codes.join(", ")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{formatMoney(m.presentValue)}</TableCell>
                  <TableCell>{m.homeArea ?? "—"}</TableCell>
                  <TableCell>{m.impliedPpsfBefore ?? "—"}</TableCell>
                  <TableCell>{m.marketValuePerSqftComps ?? "—"}</TableCell>
                  <TableCell className="max-w-[140px]">
                    {compIds.length ? (
                      <div className="flex flex-col gap-1">
                        {compIds.map((cid) => (
                          <Link
                            key={cid}
                            to={`/property/${encodeURIComponent(cid)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-primary hover:underline font-mono truncate block"
                            title={cid}
                          >
                            {cid}
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell>{formatMoney(m.estimatedSalePrice)}</TableCell>
                  <TableCell>
                    {m.flipBreakdown ? (
                      <button
                        type="button"
                        onClick={() => openFlipBreakdownModal(m)}
                        className="text-left font-medium text-primary hover:underline underline-offset-2"
                      >
                        {m.flippingCost != null ? formatMoney(m.flippingCost) : "—"}
                      </button>
                    ) : (
                      <span className="font-medium">
                        {m.flippingCost != null ? formatMoney(m.flippingCost) : "—"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {m.flipBreakdown ? (
                      <button
                        type="button"
                        onClick={() => openFlipBreakdownModal(m)}
                        className="text-left font-medium text-primary hover:underline underline-offset-2"
                      >
                        {m.grossProfit != null ? formatMoney(m.grossProfit) : "—"}
                      </button>
                    ) : (
                      <span className="font-medium">
                        {m.grossProfit != null ? formatMoney(m.grossProfit) : "—"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {m.rentEstimated ? (
                      <Badge variant="outline" className="text-[10px]">
                        est.
                      </Badge>
                    ) : (
                      "actual"
                    )}
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" asChild disabled={!r.property_id}>
                      <Link to={propertyHref} target="_blank" rel="noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="space-y-8 max-w-6xl">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Deal finder</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Scan a region (anchor property or geography) for lower $/sqft, older homes vs high $/sqft comps.
          Good vs bad uses the same flip math as the property calculator (staged construction interest, upfront
          rent, guaranteed return on present value, commission & closing on sale). Present value is refreshed
          from property detail API when possible. Condos, commercial pads (offices/shops in complexes), and
          other non–land-backed parcels are skipped (residential with private lot when lot size is known).
          Click flipping cost or gross profit for a full line-item breakdown.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New scan</CardTitle>
          <CardDescription>
            Jobs run asynchronously. Leave this page open or reopen a past job from history.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label>Region source</Label>
              <Select value={mode} onValueChange={handleModeChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="anchor">Anchor property (comparables + zip + street search, radius filter)</SelectItem>
                  <SelectItem value="geo">Geography only (search region, or ZIP / county + state)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {mode === "anchor" ? (
              <div className="space-y-2">
                <Label>Anchor property</Label>
                <AutoCompleteAddress
                  key={`anchor-${addressPickerKey}`}
                  onChange={handleAnchorAddressSelect}
                />
                {anchorPropertyId ? (
                  <p className="text-xs text-muted-foreground">
                    Selected: <span className="text-foreground font-medium">{anchorPropertyLabel || anchorPropertyId}</span>
                    {" · "}
                    <span className="tabular-nums">ID {anchorPropertyId}</span>
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Search by address; choose a result with the house icon. The property ID is filled in automatically.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Region</Label>
                  <AutoCompleteAddress
                    key={`geo-${addressPickerKey}`}
                    onChange={handleGeoAddressSelect}
                  />
                  {geoRegionLabel ? (
                    <p className="text-xs text-muted-foreground">
                      Selected region: <span className="text-foreground font-medium">{geoRegionLabel}</span>
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Choose a result with the map pin (city, ZIP, or area). Or use manual fields below.
                    </p>
                  )}
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="zip">ZIP (optional)</Label>
                    <Input
                      id="zip"
                      value={zip}
                      onChange={(e) => {
                        setZip(e.target.value);
                        clearGeoAutocompleteSelection();
                      }}
                      placeholder="33139"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="county">County (optional)</Label>
                    <Input
                      id="county"
                      value={county}
                      onChange={(e) => {
                        setCounty(e.target.value);
                        clearGeoAutocompleteSelection();
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State (optional)</Label>
                    <Input
                      id="state"
                      value={state}
                      onChange={(e) => {
                        setState(e.target.value);
                        clearGeoAutocompleteSelection();
                      }}
                      maxLength={2}
                      placeholder="FL"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground sm:col-span-3">
                    Manual ZIP, or county and state together, override a map-pin selection.
                  </p>
                </div>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Construction tier (luxury renovation $/sqft default)</Label>
                <Select value={constructionTier} onValueChange={setConstructionTier}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIER_OPTIONS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="costOv">Override $/sqft (optional)</Label>
                <Input
                  id="costOv"
                  type="number"
                  min={0}
                  value={costOverride}
                  onChange={(e) => setCostOverride(e.target.value)}
                  placeholder="Leave empty to use tier default"
                />
              </div>
            </div>

            {mode === "anchor" && (
              <div className="space-y-2">
                <Label htmlFor="radius">Radius (miles)</Label>
                <Input
                  id="radius"
                  type="number"
                  step="0.05"
                  min={0.1}
                  value={radiusMiles}
                  onChange={(e) => setRadiusMiles(e.target.value)}
                />
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Listing filter</Label>
                <Select value={marketFilter} onValueChange={setMarketFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="onMarket">On market</SelectItem>
                    <SelectItem value="offMarket">Off market</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxCand">Max candidates to evaluate</Label>
                <Input
                  id="maxCand"
                  type="number"
                  min={5}
                  max={500}
                  value={maxCandidates}
                  onChange={(e) => setMaxCandidates(e.target.value)}
                />
              </div>
            </div>

            <Button type="submit" disabled={submitting} className="gap-2">
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Running…
                </>
              ) : (
                <>
                  <Search className="h-4 w-4" />
                  Start scan
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {jobStatus && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Job status
              <Badge variant={jobStatus.status === "completed" ? "default" : "secondary"}>
                {jobStatus.status}
              </Badge>
            </CardTitle>
            <CardDescription className="font-mono text-xs">{activeJobId}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {jobStatus.error && (
              <Alert variant="destructive">
                <AlertDescription>{jobStatus.error}</AlertDescription>
              </Alert>
            )}
            {summary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <div className="text-muted-foreground">Universe</div>
                  <div className="font-semibold">{summary.universeCount}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Scored</div>
                  <div className="font-semibold">{summary.scoredCount}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Good / bad</div>
                  <div className="font-semibold">
                    {summary.goodCount} / {summary.badCount}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Skipped rows</div>
                  <div className="font-semibold">{summary.skippedCount}</div>
                </div>
              </div>
            )}
            {params && (
              <p className="text-xs text-muted-foreground">
                Mode: {params.mode}
                {params.anchorPropertyId && ` · anchor ${params.anchorPropertyId}`}
                {params.zip && ` · zip ${params.zip}`}
                {params.county && ` · ${params.county}, ${params.state}`}
                {params.geoSearchTarget?.label && !params.zip && !(params.county && params.state) && (
                  <> · region {params.geoSearchTarget.label}</>
                )}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {(resultsGood.length > 0 || resultsBad.length > 0 || resultsSkipped.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>Results</CardTitle>
            <CardDescription>
              Good = positive flip gross profit (same formula as the property calculator). Click flipping cost or
              gross profit for details.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="good">
              <TabsList>
                <TabsTrigger value="good">Good ({resultsGood.length})</TabsTrigger>
                <TabsTrigger value="bad">Bad ({resultsBad.length})</TabsTrigger>
                <TabsTrigger value="skipped">Skipped ({resultsSkipped.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="good" className="mt-4">
                {renderResultsTable(resultsGood, "good")}
              </TabsContent>
              <TabsContent value="bad" className="mt-4">
                {renderResultsTable(resultsBad, "bad")}
              </TabsContent>
              <TabsContent value="skipped" className="mt-4">
                {renderResultsTable(resultsSkipped, "skipped")}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Recent scans</CardTitle>
          <CardDescription>Your last 15 jobs (click to load results).</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {recentJobs.length === 0 && (
              <p className="text-sm text-muted-foreground">No jobs yet.</p>
            )}
            {recentJobs.map((j) => (
              <button
                key={j.id}
                type="button"
                onClick={() => loadJobFromHistory(j.id)}
                className="w-full text-left flex items-center justify-between rounded-lg border p-3 hover:bg-muted/50 text-sm"
              >
                <span className="font-mono text-xs truncate mr-2">{j.id}</span>
                <Badge variant="outline">{j.status}</Badge>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!flipModal} onOpenChange={(open) => !open && setFlipModal(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Flip calculation breakdown</DialogTitle>
            {flipModal?.address && (
              <p className="text-sm text-muted-foreground font-normal pt-1">{flipModal.address}</p>
            )}
            {flipModal?.propertyId && (
              <p className="text-xs font-mono text-muted-foreground">{flipModal.propertyId}</p>
            )}
          </DialogHeader>
          {flipModal?.breakdown && (
            <div className="space-y-3 pt-2">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                  Present value & construction
                </p>
                <BreakdownRow
                  label="Present value"
                  value={formatMoneyDetailed(flipModal.breakdown.presentValue)}
                />
                <BreakdownRow
                  label="Home area (sq ft)"
                  value={String(flipModal.breakdown.homeArea ?? "—")}
                />
                <BreakdownRow
                  label="Renovation cost ($/sq ft × area)"
                  value={formatMoneyDetailed(flipModal.breakdown.renovationCost)}
                />
                <BreakdownRow
                  label="Total construction (loan principal)"
                  value={formatMoneyDetailed(flipModal.breakdown.totalConstructionCost)}
                />
                <BreakdownRow
                  label={`Upfront construction (${flipModal.breakdown.upfrontConstructionPct ?? "—"}%)`}
                  value={formatMoneyDetailed(flipModal.breakdown.upfrontConstructionAmount)}
                />
                <BreakdownRow
                  label={`Installments (${flipModal.breakdown.paymentParts ?? "—"} × each)`}
                  value={formatMoneyDetailed(flipModal.breakdown.constructionInstallmentEach)}
                />
                <BreakdownRow
                  label="Construction loan interest (staged, investor %)"
                  value={formatMoneyDetailed(flipModal.breakdown.constructionLoanInterest)}
                />
                <BreakdownRow
                  label={`Investor return rate (annual, on construction draws)`}
                  value={
                    flipModal.breakdown.investorReturnPct != null
                      ? `${Number(flipModal.breakdown.investorReturnPct).toFixed(1)}%`
                      : "—"
                  }
                />
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                  Rent & guaranteed return
                </p>
                <BreakdownRow
                  label="Monthly rent (model)"
                  value={formatMoneyDetailed(flipModal.breakdown.monthlyRent)}
                />
                <BreakdownRow
                  label="Rental period (months)"
                  value={String(flipModal.breakdown.rentalPeriodMonths ?? "—")}
                />
                <BreakdownRow
                  label="Upfront rental (rent × months)"
                  value={formatMoneyDetailed(flipModal.breakdown.upfrontRental)}
                />
                <BreakdownRow
                  label={`Guaranteed buyer return (${flipModal.breakdown.guaranteedReturnPct ?? "—"}% of present value)`}
                  value={formatMoneyDetailed(flipModal.breakdown.guaranteedBuyerReturn)}
                />
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                  Sale-side costs & sale
                </p>
                <BreakdownRow
                  label="Market value ($/sq ft after reno)"
                  value={
                    flipModal.breakdown.marketValuePerSqft != null
                      ? `$${Number(flipModal.breakdown.marketValuePerSqft).toLocaleString()}`
                      : "—"
                  }
                />
                <BreakdownRow
                  label="Estimated sale price"
                  value={formatMoneyDetailed(flipModal.breakdown.estimatedSalePrice)}
                />
                <BreakdownRow
                  label="Agent commission (6%)"
                  value={formatMoneyDetailed(flipModal.breakdown.agentCommission)}
                />
                <BreakdownRow
                  label="Closing costs (2%)"
                  value={formatMoneyDetailed(flipModal.breakdown.closingCosts)}
                />
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                  Totals
                </p>
                <BreakdownRow
                  label="Total investment (all-in)"
                  value={formatMoneyDetailed(flipModal.breakdown.totalInvestment)}
                />
                <BreakdownRow
                  label="Total flipping cost (investment − present value)"
                  value={formatMoneyDetailed(flipModal.breakdown.totalFlippingCost)}
                />
                <BreakdownRow
                  label="Flipping cost as % of sale price"
                  value={
                    flipModal.breakdown.flippingCostPctOfSale != null
                      ? `${Number(flipModal.breakdown.flippingCostPctOfSale).toFixed(2)}%`
                      : "—"
                  }
                />
                <BreakdownRow
                  label="Gross profit (sale − total investment)"
                  value={formatMoneyDetailed(flipModal.breakdown.totalProfit)}
                />
                <BreakdownRow
                  label="Profit margin (% of sale)"
                  value={
                    flipModal.breakdown.profitMarginPct != null
                      ? `${Number(flipModal.breakdown.profitMarginPct).toFixed(2)}%`
                      : "—"
                  }
                />
              </div>
              {flipModal.engineGrossProfitLegacy != null && (
                <p className="text-xs text-muted-foreground border-t pt-3">
                  Legacy engine gross (simpler formula, pre-alignment):{" "}
                  {formatMoney(flipModal.engineGrossProfitLegacy)}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
